import PptxGenJS from 'pptxgenjs'

// ── Cadence brand constants ────────────────────────────────────────────────
const BRAND = {
  green_dark:  '1A4035',
  green:       '2D6A4F',
  green_mid:   '3D8B6A',
  green_light: 'D4EAE0',
  green_pale:  'EEF7F2',
  gold:        'C9A84C',
  charcoal:    '1C1C1C',
  slate:       '4A5568',
  silver:      'C8D5CE',
  white:       'FFFFFF',
  off_white:   'F7FAF8',
}

// ── Slide helpers ──────────────────────────────────────────────────────────
function addMasterSlide(pptx) {
  pptx.defineLayout({ name: 'CADENCE', width: 13.33, height: 7.5 })
  pptx.layout = 'CADENCE'
}

function addTitleSlide(pptx, title, subtitle, opportunity) {
  const slide = pptx.addSlide()

  // Dark green background
  slide.addShape('rect', {
    x: 0, y: 0, w: 13.33, h: 7.5,
    fill: { color: BRAND.green_dark }
  })

  // Gold accent bar
  slide.addShape('rect', {
    x: 0, y: 6.8, w: 13.33, h: 0.08,
    fill: { color: BRAND.gold }
  })

  // Left accent bar
  slide.addShape('rect', {
    x: 0.5, y: 1.5, w: 0.06, h: 3.5,
    fill: { color: BRAND.gold }
  })

  // CADENCE wordmark
  slide.addText('CADENCE', {
    x: 0.7, y: 1.5, w: 6, h: 0.6,
    fontSize: 28, bold: true,
    color: BRAND.white,
    fontFace: 'Georgia',
    charSpacing: 4
  })

  slide.addText('by Captus', {
    x: 0.7, y: 2.05, w: 4, h: 0.35,
    fontSize: 9, bold: true,
    color: BRAND.green_light,
    charSpacing: 3
  })

  // Main title
  slide.addText(title, {
    x: 0.7, y: 2.7, w: 11, h: 1.8,
    fontSize: 32, bold: true,
    color: BRAND.white,
    fontFace: 'Georgia',
    valign: 'top',
    wrap: true
  })

  // Subtitle
  if (subtitle) {
    slide.addText(subtitle, {
      x: 0.7, y: 4.6, w: 11, h: 0.5,
      fontSize: 14,
      color: BRAND.silver,
      wrap: true
    })
  }

  // Opportunity name
  if (opportunity) {
    slide.addText(opportunity, {
      x: 0.7, y: 5.2, w: 11, h: 0.4,
      fontSize: 11,
      color: BRAND.green_light,
      wrap: true
    })
  }

  // Date
  slide.addText(new Date().toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric'
  }), {
    x: 0.7, y: 6.9, w: 6, h: 0.3,
    fontSize: 9, color: BRAND.silver
  })
}

// eslint-disable-next-line no-unused-vars
function addSectionSlide(pptx, sectionTitle) {
  const slide = pptx.addSlide()

  slide.addShape('rect', {
    x: 0, y: 0, w: 13.33, h: 7.5,
    fill: { color: BRAND.green }
  })

  slide.addShape('rect', {
    x: 0, y: 6.8, w: 13.33, h: 0.08,
    fill: { color: BRAND.gold }
  })

  slide.addText(sectionTitle, {
    x: 1, y: 2.8, w: 11, h: 1.5,
    fontSize: 36, bold: true,
    color: BRAND.white,
    fontFace: 'Georgia',
    align: 'left'
  })

  addFooter(slide, sectionTitle)
}

// eslint-disable-next-line no-unused-vars
function addContentSlide(pptx, slideTitle, rows, opportunity) {
  const slide = pptx.addSlide()

  // Header bar
  slide.addShape('rect', {
    x: 0, y: 0, w: 13.33, h: 1.1,
    fill: { color: BRAND.green_dark }
  })

  // Gold accent
  slide.addShape('rect', {
    x: 0, y: 1.1, w: 13.33, h: 0.06,
    fill: { color: BRAND.gold }
  })

  // Slide title
  slide.addText(slideTitle, {
    x: 0.4, y: 0.15, w: 10, h: 0.8,
    fontSize: 18, bold: true,
    color: BRAND.white,
    fontFace: 'Georgia',
    valign: 'middle'
  })

  // CADENCE watermark top right
  slide.addText('CADENCE', {
    x: 10.5, y: 0.25, w: 2.5, h: 0.5,
    fontSize: 9, bold: true,
    color: BRAND.green_light,
    align: 'right',
    charSpacing: 2
  })

  // Content rows
  let yPos = 1.4
  rows.forEach(row => {
    if (!row.value || row.value.toString().trim() === '') return

    // Label
    slide.addText(row.label.toUpperCase(), {
      x: 0.4, y: yPos, w: 12.5, h: 0.25,
      fontSize: 7, bold: true,
      color: BRAND.green,
      charSpacing: 1
    })

    // Value
    const lines = row.value.toString().split('\n').filter(l => l.trim())
    const text = lines.join('\n')
    const lineCount = Math.min(lines.length, 8)
    const blockH = Math.max(0.35, lineCount * 0.22)

    slide.addText(text, {
      x: 0.4, y: yPos + 0.27, w: 12.5, h: blockH,
      fontSize: 11,
      color: BRAND.charcoal,
      wrap: true,
      valign: 'top'
    })

    yPos += blockH + 0.55
    if (yPos > 6.8) return // Overflow protection
  })

  addFooter(slide, opportunity)
}

function addFooter(slide, text) {
  slide.addShape('rect', {
    x: 0, y: 7.3, w: 13.33, h: 0.2,
    fill: { color: BRAND.green_pale }
  })
}

function addStandardFooter(pptx, slide, text) {
  slide.addShape('rect', {
    x: 0, y: 7.3, w: 13.33, h: 0.2,
    fill: { color: BRAND.green_pale }
  })
  if (text) {
    slide.addText(text, {
      x: 0.4, y: 7.3, w: 10, h: 0.2,
      fontSize: 7, color: BRAND.slate
    })
  }
}

// ── Content slide builder (used by all export types) ───────────────────────
function buildContentSlide(pptx, slideTitle, rows, footerText) {
  const slide = pptx.addSlide()

  slide.addShape('rect', {
    x: 0, y: 0, w: 13.33, h: 1.1,
    fill: { color: BRAND.green_dark }
  })
  slide.addShape('rect', {
    x: 0, y: 1.1, w: 13.33, h: 0.06,
    fill: { color: BRAND.gold }
  })
  slide.addText(slideTitle, {
    x: 0.4, y: 0.15, w: 10, h: 0.8,
    fontSize: 18, bold: true,
    color: BRAND.white, fontFace: 'Georgia', valign: 'middle'
  })
  slide.addText('CADENCE', {
    x: 10.5, y: 0.25, w: 2.5, h: 0.5,
    fontSize: 9, bold: true,
    color: BRAND.green_light, align: 'right', charSpacing: 2
  })

  let yPos = 1.4
  rows.forEach(row => {
    if (!row.value || row.value.toString().trim() === '') return
    if (yPos > 6.5) return

    slide.addText(row.label.toUpperCase(), {
      x: 0.4, y: yPos, w: 12.5, h: 0.25,
      fontSize: 7, bold: true, color: BRAND.green, charSpacing: 1
    })

    const lines = row.value.toString().split('\n').filter(l => l.trim())
    const blockH = Math.max(0.35, Math.min(lines.length * 0.22, 2.5))

    slide.addText(lines.join('\n'), {
      x: 0.4, y: yPos + 0.27, w: 12.5, h: blockH,
      fontSize: 11, color: BRAND.charcoal, wrap: true, valign: 'top'
    })

    yPos += blockH + 0.55
  })

  addStandardFooter(pptx, slide, footerText)
  return slide
}

function addSectionDivider(pptx, title, footerText) {
  const slide = pptx.addSlide()
  slide.addShape('rect', {
    x: 0, y: 0, w: 13.33, h: 7.5,
    fill: { color: BRAND.green }
  })
  slide.addShape('rect', {
    x: 0, y: 6.8, w: 13.33, h: 0.08,
    fill: { color: BRAND.gold }
  })
  slide.addText(title, {
    x: 1, y: 2.8, w: 11, h: 1.5,
    fontSize: 36, bold: true,
    color: BRAND.white, fontFace: 'Georgia'
  })
  addStandardFooter(pptx, slide, footerText)
}

// ── Export functions ───────────────────────────────────────────────────────

export function exportKickoffPack(artefact, opportunity) {
  const pptx = new PptxGenJS()
  addMasterSlide(pptx)
  const c = artefact.content || {}
  const oppName = opportunity?.title || 'Opportunity'
  const footer = `${oppName} · Bid Kick-off Pack · Confidential`

  addTitleSlide(pptx, 'Bid Kick-off Pack', opportunity?.buyer || '', oppName)

  addSectionDivider(pptx, 'Opportunity Overview', footer)
  buildContentSlide(pptx, 'Opportunity Overview', [
    { label: 'Opportunity Summary', value: c.opportunity_summary },
    { label: 'Key Evaluation Criteria', value: c.evaluation_criteria },
  ], footer)
  buildContentSlide(pptx, 'Key Dates & Timeline', [
    { label: 'Key Dates', value: c.key_dates },
  ], footer)

  addSectionDivider(pptx, 'Our Position', footer)
  buildContentSlide(pptx, 'Win Themes & Discriminators', [
    { label: 'Primary Win Theme', value: c.win_theme_primary },
    { label: 'Supporting Win Themes', value: c.win_themes_supporting },
    { label: 'Key Discriminators', value: c.discriminators },
  ], footer)
  buildContentSlide(pptx, 'Risks to Winning', [
    { label: 'Known Risks', value: c.risks_to_winning },
  ], footer)

  addSectionDivider(pptx, 'Bid Team', footer)
  buildContentSlide(pptx, 'Team Structure', [
    { label: 'Bid Lead', value: c.bid_lead },
    { label: 'Solution Lead', value: c.solution_lead },
    { label: 'Commercial Lead', value: c.commercial_lead },
    { label: 'Full Team', value: c.team_structure },
  ], footer)

  addSectionDivider(pptx, 'Ground Rules', footer)
  buildContentSlide(pptx, 'Bid Rules & Escalation', [
    { label: 'Bid Rules & Protocols', value: c.ground_rules },
    { label: 'Escalation Route', value: c.escalation },
  ], footer)

  pptx.writeFile({ fileName: `Kick-off Pack — ${oppName}.pptx` })
}

export function exportBlueTeam(artefact, opportunity) {
  const pptx = new PptxGenJS()
  addMasterSlide(pptx)
  const c = artefact.content || {}
  const oppName = opportunity?.title || 'Opportunity'
  const footer = `${oppName} · Blue Team Review · Confidential`

  addTitleSlide(pptx, 'Blue Team Review', opportunity?.buyer || '', oppName)

  addSectionDivider(pptx, 'Session Details', footer)
  buildContentSlide(pptx, 'Session & Attendees', [
    { label: 'Session Date & Facilitator', value: c.session_details },
    { label: 'Attendees', value: c.attendees },
  ], footer)

  addSectionDivider(pptx, 'Win Theme Testing', footer)
  buildContentSlide(pptx, 'Win Theme Under Review', [
    { label: 'Win Theme', value: c.win_theme },
    { label: 'Supporting Evidence', value: c.theme_evidence },
  ], footer)
  buildContentSlide(pptx, 'Challenges & Actions', [
    { label: 'Challenges Raised', value: c.challenges },
    { label: 'Actions from Challenges', value: c.challenge_actions },
  ], footer)

  addSectionDivider(pptx, 'Solution Challenge', footer)
  buildContentSlide(pptx, 'Solution Assessment', [
    { label: 'Strengths Confirmed', value: c.solution_strengths },
    { label: 'Weaknesses Identified', value: c.solution_weaknesses },
    { label: 'Gaps to Address', value: c.solution_gaps },
  ], footer)

  addSectionDivider(pptx, 'Evaluator Perspective', footer)
  buildContentSlide(pptx, 'Evaluator View & Scores', [
    { label: 'How the Evaluator Will Read Our Response', value: c.evaluator_view },
    { label: 'Likely Scores', value: c.likely_scores },
    { label: 'What Would Improve Our Scores', value: c.score_improvements },
  ], footer)

  addSectionDivider(pptx, 'Actions', footer)
  buildContentSlide(pptx, 'Action Log', [
    { label: 'Actions & Owners', value: c.action_log },
  ], footer)

  pptx.writeFile({ fileName: `Blue Team Review — ${oppName}.pptx` })
}

export function exportBlackHat(artefact, opportunity) {
  const pptx = new PptxGenJS()
  addMasterSlide(pptx)
  const c = artefact.content || {}
  const oppName = opportunity?.title || 'Opportunity'
  const footer = `${oppName} · Black Hat Review · Confidential`

  addTitleSlide(pptx, 'Black Hat Review', opportunity?.buyer || '', oppName)

  addSectionDivider(pptx, 'Our Vulnerabilities', footer)
  buildContentSlide(pptx, 'Our Weaknesses & Mitigations', [
    { label: 'Our Biggest Weaknesses', value: c.our_vulnerabilities },
    { label: 'How We Mitigate Each', value: c.vulnerability_mitigation },
  ], footer)

  const competitors = c.competitors || []
  if (competitors.length > 0) {
    addSectionDivider(pptx, 'Competitor Analysis', footer)
    competitors.forEach((comp, idx) => {
      if (!comp.name) return
      buildContentSlide(pptx, `Competitor: ${comp.name}`, [
        { label: 'Threat Level', value: `${comp.threat_level} / 5` },
        { label: 'Their Likely Win Themes', value: comp.win_themes },
        { label: 'Their Discriminators Against Us', value: comp.discriminators_against_us },
        { label: 'Their Weaknesses', value: comp.their_weaknesses },
        { label: 'Our Ghost Strategy', value: comp.ghost_strategy },
      ], footer)
    })
  }

  addSectionDivider(pptx, 'Overall Strategy', footer)
  buildContentSlide(pptx, 'Competitive Strategy', [
    { label: 'Our Competitive Position', value: c.competitive_summary },
    { label: 'Key Strategic Moves', value: c.strategic_moves },
  ], footer)

  pptx.writeFile({ fileName: `Black Hat Review — ${oppName}.pptx` })
}

export function exportRedTeam(artefact, opportunity) {
  const pptx = new PptxGenJS()
  addMasterSlide(pptx)
  const c = artefact.content || {}
  const oppName = opportunity?.title || 'Opportunity'
  const footer = `${oppName} · Red Team Review · Confidential`

  addTitleSlide(pptx, 'Red Team Review', opportunity?.buyer || '', oppName)

  addSectionDivider(pptx, 'Review Details', footer)
  buildContentSlide(pptx, 'Review Session', [
    { label: 'Review Date & Chair', value: c.review_details },
    { label: 'Reviewers', value: c.reviewers },
    { label: 'Overall Impression', value: c.overall_impression },
  ], footer)

  const questions = c.question_reviews || []
  if (questions.length > 0) {
    addSectionDivider(pptx, 'Question Reviews', footer)
    questions.forEach((q) => {
      if (!q.question_ref && !q.strengths) return
      buildContentSlide(pptx, `Question Review: ${q.question_ref || 'Unnamed'}`, [
        { label: 'Score', value: q.score_out_of_10 ? `${q.score_out_of_10} / 10` : '' },
        { label: 'Compliance Pass', value: q.compliance_pass },
        { label: 'Win Theme Clear', value: q.win_theme_clear },
        { label: 'Strengths', value: q.strengths },
        { label: 'Improvements Required', value: q.improvements },
        { label: 'Must Fix Before Submission', value: q.must_fix },
      ], footer)
    })
  }

  addSectionDivider(pptx, 'Overall Assessment', footer)
  buildContentSlide(pptx, 'Strengths & Weaknesses', [
    { label: 'Response Strengths', value: c.overall_strengths },
    { label: 'Response Weaknesses', value: c.overall_weaknesses },
  ], footer)
  buildContentSlide(pptx, 'Priority Actions & Recommendation', [
    { label: 'Priority Actions Before Submission', value: c.priority_actions },
    { label: 'Red Team Recommendation', value: c.recommendation },
  ], footer)

  pptx.writeFile({ fileName: `Red Team Review — ${oppName}.pptx` })
}

export function exportSignOffPack(artefact, opportunity) {
  const pptx = new PptxGenJS()
  addMasterSlide(pptx)
  const c = artefact.content || {}
  const oppName = opportunity?.title || 'Opportunity'
  const footer = `${oppName} · Sign-Off Pack · Strictly Confidential`

  addTitleSlide(pptx, 'Sign-Off Pack', opportunity?.buyer || '', oppName)

  addSectionDivider(pptx, 'Opportunity Recap', footer)
  buildContentSlide(pptx, 'Opportunity & Recommendation', [
    { label: 'Opportunity Summary', value: c.opportunity_recap },
    { label: 'Qualification Score & PWin', value: c.qual_summary },
    { label: 'Bid Lead Recommendation', value: c.submit_recommendation },
  ], footer)

  addSectionDivider(pptx, 'Solution Summary', footer)
  buildContentSlide(pptx, 'Our Proposed Solution', [
    { label: 'Solution Summary', value: c.solution_summary },
    { label: 'Key Personnel', value: c.key_personnel },
    { label: 'Mobilisation Approach', value: c.mobilisation },
  ], footer)

  addSectionDivider(pptx, 'Commercial Position', footer)
  buildContentSlide(pptx, 'Commercial Summary', [
    { label: 'Total Bid Price', value: c.bid_price },
    { label: 'Estimated Cost', value: c.estimated_cost },
    { label: 'Gross Margin', value: c.gross_margin },
    { label: 'Key Commercial Assumptions', value: c.commercial_assumptions },
    { label: 'Pricing Confidence', value: c.pricing_confidence },
  ], footer)

  addSectionDivider(pptx, 'Risk Summary', footer)
  buildContentSlide(pptx, 'Risk Register', [
    { label: 'Top Risks', value: c.top_risks },
    { label: 'Accepted Risks', value: c.accepted_risks },
  ], footer)

  addSectionDivider(pptx, 'Commercial & Legal', footer)
  buildContentSlide(pptx, 'Legal & Commercial Considerations', [
    { label: 'Onerous Terms', value: c.onerous_terms },
    { label: 'Deviations', value: c.deviations },
    { label: 'Liability Position', value: c.liability },
    { label: 'TUPE Implications', value: c.tupe },
    { label: 'Legal Sign-Off', value: c.legal_signoff },
  ], footer)

  addSectionDivider(pptx, 'Authority Sign-Off', footer)
  buildContentSlide(pptx, 'Sign-Off & Decision', [
    { label: 'Authorising Body', value: c.authority },
    { label: 'Conditions on Approval', value: c.approval_conditions },
    { label: 'Decision', value: c.decision },
    { label: 'Decision Date', value: c.decision_date },
    { label: 'Decision Notes', value: c.decision_notes },
  ], footer)

  pptx.writeFile({ fileName: `Sign-Off Pack — ${oppName}.pptx` })
}

export function exportQuestionStoryboard(artefact, opportunity) {
  const pptx = new PptxGenJS()
  addMasterSlide(pptx)
  const c = artefact.content || {}
  const oppName = opportunity?.title || 'Opportunity'
  const footer = `${oppName} · Question Storyboard · Confidential`

  addTitleSlide(pptx, 'Question Storyboard', opportunity?.buyer || '', oppName)

  addSectionDivider(pptx, 'Response Strategy', footer)
  buildContentSlide(pptx, 'Overall Response Strategy', [
    { label: 'ITT Reference', value: c.itt_ref },
    { label: 'Response Strategy', value: c.response_strategy },
  ], footer)

  const questions = c.questions || []
  if (questions.length > 0) {
    addSectionDivider(pptx, 'Question Storyboards', footer)
    questions.forEach((q, idx) => {
      buildContentSlide(pptx,
        `Q${idx + 1}${q.question_ref ? ` — ${q.question_ref}` : ''}`,
        [
          { label: 'Question', value: q.question_text },
          { label: 'Limit', value: q.word_limit },
          { label: 'Weighting', value: q.weighting },
          { label: 'Win Theme', value: q.win_theme },
          { label: 'Discriminators', value: q.discriminators },
          { label: 'Proof Points', value: q.proof_points },
          { label: 'Response Structure', value: q.response_structure },
          { label: 'Compliance Items', value: q.compliance_items },
          { label: 'Graphics Plan', value: q.graphics_plan },
          { label: 'Assigned Writer', value: q.assigned_writer },
        ],
        footer
      )
    })
  }

  pptx.writeFile({ fileName: `Question Storyboard — ${oppName}.pptx` })
}

// ── Main export dispatcher ─────────────────────────────────────────────────
export function exportArtefact(artefact, opportunity) {
  switch (artefact.type) {
    case 'kickoff_pack':        return exportKickoffPack(artefact, opportunity)
    case 'question_storyboard': return exportQuestionStoryboard(artefact, opportunity)
    case 'blue_team':           return exportBlueTeam(artefact, opportunity)
    case 'black_hat':           return exportBlackHat(artefact, opportunity)
    case 'red_team':            return exportRedTeam(artefact, opportunity)
    case 'sign_off_pack':       return exportSignOffPack(artefact, opportunity)
    default: console.warn('Unknown artefact type:', artefact.type)
  }
}